import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { BarcodeReader } from '../../helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function BarcodeScanner({ onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanInterval = useRef(null);
  const { t } = useTranslation(['description']);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [barcodeReader, setBarcodeReader] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);

  const stopCurrentStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    async function fetchCameras() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        stream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');

        setCameras(videoInputs);

        const backCam =
          videoInputs.find(
            (cam) =>
              /back|rear|environment/i.test(cam.label) &&
              !/macro|zoom|tele|wide/i.test(cam.label)
          ) ||
          videoInputs.find((cam) => /back|rear|environment/i.test(cam.label)) ||
          videoInputs[0];

        if (backCam) {
          setSelectedCameraId(backCam.deviceId);
        } else {
          setError(t('barcode.description_error', { ns: 'description' }));
        }
      } catch (e) {
        setError(
          t('barcode.description_error_get', { ns: 'description' }) + e.message
        );
      }
    }

    fetchCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function initBarcodeReader() {
      try {
        const { barcodeReaderError } = await BarcodeReader.setup();
        if (barcodeReaderError) {
          setError(t('barcode.description_error_init', { ns: 'description' }));
          return;
        }

        const reader = await BarcodeReader.create();
        setBarcodeReader(reader);
      } catch (e) {
        setError(
          `${t('barcode.description_error_init', { ns: 'description' })} ${
            e.message
          }`
        );
      }
    }

    initBarcodeReader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCameraId || !videoRef.current) return;

    let cancelled = false;

    const startStream = async () => {
      stopCurrentStream();
      setScanning(false);
      setError(null);
      setLastResult(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCameraId } },
          audio: false,
        });

        if (!cancelled && videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          await videoRef.current.play();
          setScanning(true);
        }
      } catch (e) {
        setError(
          `${t('barcode.description_error_start', { ns: 'description' })} ${
            e.message
          }`
        );
      }
    };

    startStream();

    return () => {
      cancelled = true;
      stopCurrentStream();
      if (scanInterval.current) {
        clearInterval(scanInterval.current);
        scanInterval.current = null;
      }
    };
  }, [selectedCameraId, t]);

  useEffect(() => {
    if (!scanning || !barcodeReader || !videoRef.current) return;

    scanInterval.current = setInterval(async () => {
      try {
        const result = await barcodeReader.detect(videoRef.current);
        if (result?.rawValue) {
          const code = result.rawValue;
          if (code !== lastResult) {
            setLastResult(code);
            onDetected && onDetected(code);
          }
        }
      } catch {
        // Errors are expected if no barcode is detected in the frame.
        // We can ignore them to allow for continuous scanning.
      }
    }, 500);

    return () => {
      if (scanInterval.current) {
        clearInterval(scanInterval.current);
        scanInterval.current = null;
      }
    };
  }, [scanning, barcodeReader, lastResult, onDetected]);

  return (
    <div>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel id="select-camera">
          {t('barcode.select_cam', { ns: 'description' })}
        </InputLabel>
        <Select
          labelId="select-camera-label"
          id="select-camera"
          value={selectedCameraId || ''}
          label="Age"
          onChange={(e) => setSelectedCameraId(e.target.value)}
        >
          {cameras.map((cam) => (
            <MenuItem key={cam.deviceId} value={cam.deviceId}>
              {cam.label || `Cámara ${cam.deviceId}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <video
        ref={videoRef}
        style={{
          width: '100%',
          maxWidth: 400,
          marginTop: 10,
          borderRadius: 8,
        }}
        muted
        autoPlay
        playsInline
      />
    </div>
  );
}
